import { merge } from 'lodash-es';
import type { Action } from 'state/actions';
import { reorderItem } from 'state/userPreferences/tabsOrder/utils';
import { addOrPatchKey, patchKey } from 'state/utils/collection';

import type { WorkspacesStateMap } from './workspaces.model';
import { initialWorkspacesState as initState, isWorkspaceWithProductSelector } from './workspaces.model';

export function workspacesReducer(state: WorkspacesStateMap = initState, action: Action): WorkspacesStateMap {
  switch (action.type) {
    case 'WORKSPACE_TAB_CREATED':
      return patchKey(state, action.workspace, workspaceState => {
        const newTabs = action.insertAtStart
          ? [action.id, ...workspaceState.tabs]
          : [...workspaceState.tabs, action.id];
        if (isWorkspaceWithProductSelector(action.workspace)) {
          return {
            activeTab: action.id,
            tabs: newTabs,
            recentQuotedProductVariations: { status: 'loading' as const },
            selectedProductVariation: undefined,
            selectedClient: undefined,
            selectedProductSelectorTab: 'catalogue',
          };
        }
        return {
          activeTab: action.id,
          tabs: newTabs,
        };
      });
    case 'WORKSPACE_ADVISORY_TAB_CREATED':
      return patchKey(state, action.workspace, workspaceState => ({
        activeTab: action.id,
        tabs: [...workspaceState.tabs, action.id],
      }));
    case 'WORKSPACE_TAB_CLONED':
      return patchKey(state, action.workspace, workspaceState => ({
        activeTab: action.newTabId,
        tabs: insertTabAfter(workspaceState.tabs, action.oldTabId, action.newTabId),
      }));
    case 'WORKSPACE_QUOTE_RELOADED':
      return patchKey(state, action.workspace, workspaceState => ({
        activeTab: action.temporaryFormId,
        tabs: [action.temporaryFormId, ...workspaceState.tabs],
      }));
    case 'WORKSPACE_TAB_QUOTE_REPRICED':
      return patchKey(state, action.workspace, workspaceState => ({
        activeTab: action.tabId,
        tabs: [...workspaceState.tabs, action.tabId],
      }));
    case 'WORKSPACE_RFQ_LOAD_REQUESTED':
      return patchKey(state, action.workspace, workspaceState => ({
        activeTab: action.tabId,
        tabs: [...workspaceState.tabs, action.tabId],
      }));
    case 'WORKSPACE_TAB_ACTIVATED':
      return patchKey(state, action.workspace, () => {
        if (isWorkspaceWithProductSelector(action.workspace)) {
          return {
            activeTab: action.tabId,
            recentQuotedProductVariations: { status: 'loading' as const },
          };
        }
        return { activeTab: action.tabId };
      });
    case 'WORKSPACE_TAB_UNACTIVATED':
      return patchKey(state, action.workspace, () => ({
        activeTab: undefined,
      }));
    case 'WORKSPACE_TAB_MOVED':
      const tabs = reorderItem(state[action.workspace].tabs, action.sourceIndex, action.targetIndex);
      return patchKey(state, action.workspace, () => ({ tabs }));
    case 'WORKSPACE_TABS_ALL_REMOVED':
      return patchKey(state, action.workspace, () => ({
        tabs: [],
        activeTab: undefined,
      }));
    case 'WORKSPACE_TAB_REMOVED':
      return patchKey(state, action.workspace, workspaceState => {
        const newTabIds = workspaceState.tabs.filter(tabId => tabId !== action.id);

        let newActiveTab: string | undefined = workspaceState.activeTab;

        if (action.id === workspaceState.activeTab) {
          const pricerIndex = workspaceState.tabs.indexOf(action.id);
          const wasLastPricer = pricerIndex === workspaceState.tabs.length - 1;

          const newActiveTabIndex = wasLastPricer ? pricerIndex - 1 : pricerIndex;

          newActiveTab = newActiveTabIndex >= 0 ? newTabIds[newActiveTabIndex] : undefined;
        }

        return {
          ...workspaceState,
          activeTab: newActiveTab,
          tabs: newTabIds,
        };
      });
    case 'WORKSPACE_RESET_TAB_COMPLETED':
      return patchKey(state, action.workspace, workspaceState => ({
        activeTab: workspaceState.activeTab === action.id ? action.newId : workspaceState.activeTab,
        tabs: workspaceState.tabs.map(id => (id === action.id ? action.newId : id)),
      }));
    case 'WORKSPACE_TABS_FETCH_REQUESTED':
      return patchKey(state, action.workspace, () => ({
        status: 'loading' as const,
      }));
    case 'WORKSPACE_TABS_FETCHED':
      return patchKey(state, action.workspace, () => ({
        status: 'loaded' as const,
      }));

    case 'WORKSPACE_PERSISTED_TABS_APPLIED':
      return patchKey(state, action.workspace, () => ({
        activeTab: action.activeTabId,
        tabs: action.tabs.map(tab => tab.id),
        isTabsPanelShown: action.isEssentialTabsPanelShown,
        status: 'ready' as const,
      }));
    case 'WORKSPACE_SHOW_DASHBOARD':
      return patchKey(state, action.workspace, () => ({
        activeQuoteDashboard: 'show' as const,
      }));
    case 'WORKSPACE_HIDE_DASHBOARD':
      return patchKey(state, action.workspace, () => ({
        activeQuoteDashboard: 'hide' as const,
      }));

    case 'WORKSPACE_PRODUCT_VARIATION_SELECTED':
      return patchKey(state, action.workspace, () => {
        if (isWorkspaceWithProductSelector(action.workspace)) {
          return {
            selectedProductVariation: action.selectedProductVariation,
            selectedAssetTypeFilter:
              state[action.workspace].selectedProductSelectorTab !== 'catalogue'
                ? 'All'
                : state[action.workspace].selectedAssetTypeFilter,
          };
        }
        return {};
      });
    case 'WORKSPACE_PRODUCT_SELECTOR_TAB_SELECTED':
      return patchKey(state, action.workspace, () => {
        if (isWorkspaceWithProductSelector(action.workspace)) {
          return {
            selectedProductSelectorTab: action.tabType,
          };
        }
        return {};
      });
    case 'WORKSPACE_CLIENT_SELECTED':
      return patchKey(state, action.workspace, () => {
        if (isWorkspaceWithProductSelector(action.workspace)) {
          return {
            selectedClient: action.client,
          };
        }
        return {};
      });
    case 'WORKSPACE_LOAD_FROM_LOCAL_STORAGE':
      return merge({}, state, action.payload);

    case 'WORKSPACE_RECENT_PRODUCT_VARIATION_RECEIVED':
      return Object.entries(action.recentProductsByWorkspace).reduce(
        (newState, [workspace, recentProductsVariations]) => {
          return addOrPatchKey(newState, workspace, () => {
            if (isWorkspaceWithProductSelector(workspace)) {
              return {
                recentQuotedProductVariations: {
                  status: 'success' as const,
                  payload: recentProductsVariations,
                },
              };
            }
            return {};
          });
        },
        state,
      );

    case 'WORKSPACE_RECENT_PRODUCT_VARIATION_FETCH_FAILED':
      return patchKey(state, action.workspace, () => {
        if (isWorkspaceWithProductSelector(action.workspace)) {
          return {
            recentQuotedProductVariations: {
              status: 'error' as const,
              error: action.error,
            },
          };
        }
        return {};
      });

    case 'PRODUCTS_REFERENTIAL_LOADED':
      return {
        ...state,
        static: {
          ...state.static,
          selectedAssetTypeFilter: 'All',
        },
        newBatch: {
          ...state.newBatch,
          selectedAssetTypeFilter: 'All',
        },
      };
    case 'DYNAMIC_PRODUCTS_LOADED':
      return {
        ...state,
        dynamic: {
          ...state.dynamic,
          selectedAssetTypeFilter: 'All',
        },
      };
    case 'WORKSPACE_PRODUCT_SELECTOR_FILTER_SELECTED':
      return patchKey(state, action.workspace, () => {
        if (isWorkspaceWithProductSelector(action.workspace)) {
          return {
            selectedAssetTypeFilter: action.assetType,
            selectedProductVariation: {},
          };
        }
        return {};
      });
    case 'ESSENTIAL_WORKSPACE_SHOW_TABS_PANEL_CHANGED':
      return {
        ...state,
        essential: { ...state.essential, isTabsPanelShown: !state.essential.isTabsPanelShown },
      };
    default:
      return state;
  }
}

function insertTabAfter(tabs: string[], oldTabId: string, newTabId: string): string[] {
  const index = tabs.indexOf(oldTabId);
  if (index === -1) {
    return tabs;
  }
  return [...tabs.slice(0, index + 1), newTabId, ...tabs.slice(index + 1)];
}
